/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useGetClients from '@/hooks/useGetClients';
import { cn } from '@/lib/utils';
import { formatColumnName } from '@/utils/formatColumnName';
import { ChevronDown, ChevronUp, Edit3, Search, UserPlus } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';
import ClientTableRow from './ClientTableRow';
import useBulkInviteClients from '@/hooks/useBulkInviteClients';
import { toast } from 'sonner';
import useGetProfile from '@/hooks/useGetProfile';
import AddClientModal from './AddClientModal';

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  column: string;
  direction: SortDirection;
}

interface Props {
  onImportData: () => void;
}

const ImportedDataView: FC<Props> = ({ onImportData }) => {
  const [sortState, setSortState] = useState<SortState>({
    column: '',
    direction: null,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [localData, setLocalData] = useState<any[]>([]);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);

  const { data } = useGetClients();
  const { data: profile } = useGetProfile();
  const { mutate: bulkInvite, isPending: inviteLoading } =
    useBulkInviteClients();

  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  // Add new columns to each row if they don't exist
  const enrichedData = useMemo(() => {
    return localData.map((row) => ({
      ...row,
      inviteSent: row.inviteSent || null,
      registeredAt: row.registeredAt || null,
    }));
  }, [localData]);

  // Get all possible columns from the original data for consistency
  const allPossibleColumns = useMemo(() => {
    if (data?.length === 0) return [];
    const allKeys = new Set<string>();
    // data.forEach((row) => {
    //   Object.keys(row).forEach((key) => allKeys.add(key));
    // });
    allKeys.add('firstName');
    allKeys.add('lastName');
    allKeys.add('emailAddress');
    allKeys.add('emailAddress2');
    allKeys.add('inviteSent');
    allKeys.add('registeredAt');
    return Array.from(allKeys).filter((key) => key !== 'id');
  }, [data]);

  // Use all possible columns for consistency
  const columns = allPossibleColumns;

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = enrichedData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    // Apply sorting
    if (sortState.column && sortState.direction) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortState.column];
        const bVal = b[sortState.column];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (sortState.direction === 'asc') {
          return String(aVal).localeCompare(String(bVal));
        } else {
          return String(bVal).localeCompare(String(aVal));
        }
      });
    }

    return filtered;
  }, [enrichedData, searchTerm, sortState]);

  const handleSort = (column: string) => {
    setSortState((prev) => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { column: '', direction: null };
        }
      }
      return { column, direction: 'asc' };
    });
  };

  const getSortIcon = (column: string) => {
    if (sortState.column !== column) return null;
    return sortState.direction === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleInviteAll = () => {
    const clientsToInvite = processedData.filter((data) => !data.registeredAt);

    bulkInvite(
      {
        invites: clientsToInvite.map((c) => ({
          clientId: c._id,
          clientFirstName: c.firstName,
          clientLastName: c.lastName,
          emailAddress: c.emailAddress,
        })),
      },
      {
        onSuccess: () => {
          toast.success('Bulk invite sent.');
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Imported Data ({processedData.length} records)
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={handleInviteAll}
                disabled={inviteLoading || processedData.length === 0}
              >
                Invite All
              </Button>
              <Button variant="outline" onClick={() => setIsAddClientOpen(true)}>
                Add Client
              </Button>
              <Button variant="outline" onClick={onImportData}>
                Import Data
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-2">
            <div className="flex items-center gap-2 w-full md:w-fit">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Badge variant="outline" className="ml-4">
              <Edit3 className="h-3 w-3 mr-1" />
              Click any row to edit
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          <div className="w-full overflow-x-auto rounded-md border pt-10">
            <Table className="min-w-[1000px] w-full">
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead
                      key={column}
                      className={cn(
                        'cursor-pointer select-none hover:bg-muted/50 transition-colors',
                        'font-semibold',
                      )}
                      onClick={() => handleSort(column)}
                    >
                      <div className="flex items-center justify-between">
                        <span>{formatColumnName(column)}</span>
                        <div className="ml-2 flex-shrink-0">
                          {getSortIcon(column)}
                        </div>
                      </div>
                    </TableHead>
                  ))}
                  {profile?.role === 'AGENT' && (
                    <TableHead
                      className={cn(
                        'cursor-pointer select-none hover:bg-muted/50 transition-colors',
                        'font-semibold',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span>Actions</span>
                      </div>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {searchTerm
                        ? 'No records found matching your search.'
                        : 'No data to display.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  processedData.map((row, index) => (
                    <ClientTableRow columns={columns} row={row} key={index} />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {processedData.length > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {processedData.length} of {enrichedData.length} records
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          )}
        </CardContent>
      </Card>
      <AddClientModal
        isOpen={isAddClientOpen}
        onClose={() => setIsAddClientOpen(false)}
      />
    </div>
  );
};

export default ImportedDataView;
